import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { LetDirective } from '@ngrx/component';
import { isEqual } from 'lodash';
import { Observable, of } from 'rxjs';
import { distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';

import { DatePipesModule, HeaderElementDirective, ReactiveMap, filterNil } from '@core/shared/util';
import {
  AsideFiltersComponent,
  ConfirmDialogData,
  ConfirmDialogService,
  ListHeaderComponent,
  RootModuleNavComponent,
  SpinnerComponent,
} from '@core/ui';
import {
  CreatePlatformCatalog,
  PlatformCatalog,
  PlatformCatalogFilter,
  PlatformCatalogGlobalFilter,
  PlatformcatalogSearchQueryField,
} from '@mp/content-manager/plattformkataloge/data-access';
import { QueryParamsStoreFilterFacade } from '@mp/shared/data-access';
import { LoginContextService } from '@mpk/shared/data-access';

import { ActionButtonComponent } from '../action-button/action-button.component';
import { ActionsSlideToggleButtonComponent } from '../actions-slide-toggle-button/actions-slide-toggle-button.component';
import { BadgeUpdatesComponent } from '../badge-updates/badge-updates.component';
import { PlattformkatalogCreateComponent } from '../create/plattformkatalog-create.component';
import { PlattformKatalogeAktualisierungenFacade } from '../facades/plattformkatalog-aktualisierungen.facade';
import { PlattformkatalogeFacade } from '../facades/plattformkataloge.facade';
import { PlatformCatalogsPermissions } from '../permissions';
import { PlatformCatalogsSearchBarComponent } from '../platformcatalogs-search-bar/platformcatalogs-search-bar.component';
import { PublishButtonComponent } from '../publish-button/publish-button.component';

import { PlattformkatalogeFilterAreaComponent } from './filter-area/plattformkataloge-filter-area.component';
import { PlatformCatalogCountInfoPipe } from './platformcatalog-countinfo.pipe';
import {
  PlatformCatalogPanelComponent,
  PlatformCatalogPanelTab,
} from './platformcatalog-panel/platformcatalog-panel.component';
import { PlatformCatalogsAsideFiltersSummaryPipe } from './platformcatalogs-aside-filters-summary.pipe';

@Component({
  selector: 'mp-plattformkataloge-list',
  templateUrl: './plattformkataloge-list.component.html',
  styleUrl: './plattformkataloge-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    LetDirective,
    RouterOutlet,

    MatButton,
    MatIcon,
    MatTooltip,
    MatExpansionModule,

    RootModuleNavComponent,
    HeaderElementDirective,
    PlatformCatalogsSearchBarComponent,
    AsideFiltersComponent,
    PlattformkatalogeFilterAreaComponent,
    ListHeaderComponent,
    PlattformkatalogCreateComponent,
    PlatformCatalogPanelComponent,
    BadgeUpdatesComponent,
    PublishButtonComponent,
    SpinnerComponent,
    PlatformCatalogCountInfoPipe,
    PlatformCatalogsAsideFiltersSummaryPipe,
    DatePipesModule,
    ActionsSlideToggleButtonComponent,
    ActionButtonComponent,
  ],
  providers: [PlattformKatalogeAktualisierungenFacade],
})
export class PlattformkatalogeListComponent implements OnInit {
  protected editMode: string | true | null = null;

  protected readonly hasManagePlatformCatalogsPermission = this.loginContextService.permission(
    PlatformCatalogsPermissions.ManagePlatformCatalogs,
  );
  protected readonly hasPublishCatalogsPermission = this.loginContextService.permission(
    PlatformCatalogsPermissions.PublishCatalogs,
  );

  readonly plattformKataloge$: Observable<readonly PlatformCatalog[]> = this.plattformKatalogeFacade.kataloge$;

  readonly plattformkatalogFilter$: Observable<PlatformCatalogFilter> = this.plattformkatalogFilterFacade.filter$;
  readonly dataSources$: Observable<string[]> = this.plattformKatalogeFacade.dataSources$;

  //Variables to set and reset searchbar values by filter
  searchTerm = '';
  field = PlatformcatalogSearchQueryField.All;

  readonly isLoading$: Observable<boolean> = this.plattformKatalogeFacade.isLoading$;

  readonly aktualisierungen$: ReactiveMap<string, number> = this.aktualisierungenFacade.aktualisierungen$;
  readonly deletedAktualisierungen$: ReactiveMap<string, number> = this.aktualisierungenFacade.deletedAktualisierungen$;
  readonly dirty$: ReactiveMap<string, boolean> = this.aktualisierungenFacade.dirty$;

  protected readonly activePlatformCatalogId$: Observable<string | null>;

  protected readonly editModeFormControl: FormControl<string>;

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly router: Router,
    private readonly aktualisierungenFacade: PlattformKatalogeAktualisierungenFacade,
    private readonly plattformKatalogeFacade: PlattformkatalogeFacade,
    private readonly plattformkatalogFilterFacade: QueryParamsStoreFilterFacade<PlatformCatalogFilter>,
    formBuilder: FormBuilder,
    private readonly destroyRef: DestroyRef,
    private readonly loginContextService: LoginContextService,
    private readonly confirmDialogService: ConfirmDialogService,
  ) {
    this.plattformkatalogFilterFacade.filter$
      .pipe(
        map((filter) => ({
          searchTerm: filter.searchTerm,
          catalogType: filter.catalogType,
          onlyUpdated: filter.updatedOnly,
          field: filter.field,
          dataSources: filter.dataSources,
        })),
        distinctUntilChanged(isEqual),
        takeUntilDestroyed(),
      )
      .subscribe({
        next: (filter) => {
          this.plattformKatalogeFacade.getAllPlattformKataloge(filter);
          this.searchTerm = filter.searchTerm;
          this.field = filter.field;
        },
      });

    this.plattformKatalogeFacade.getAllDataSources();

    this.activePlatformCatalogId$ = this.activatedRoute.paramMap.pipe(
      map((params) => params.get('platformCatalogId') || null),
      tap((platformCatalogId) => {
        // Update filter state for current catalog. Especially useful for initial load by url
        if (platformCatalogId) {
          this.plattformKatalogeFacade.updateAndGetFilterForPlatformCatalog(platformCatalogId);
        }
      }),
    );

    this.editModeFormControl = formBuilder.control('', {
      nonNullable: true,
      validators: [Validators.required, Validators.pattern(new RegExp('\\S'))],
    });
  }

  ngOnInit(): void {
    // If catalog not found, navigate to list
    this.activePlatformCatalogId$
      .pipe(
        filterNil(),
        switchMap((platformCatalogId) => this.plattformKatalogeFacade.getPlatformCatalogById(platformCatalogId)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        error: () => {
          this.internalNavigateToList(true);
        },
      });
  }

  private isCatalogActive(platformCatalogId: string): boolean {
    return this.activatedRoute.snapshot.paramMap.get('platformCatalogId') === platformCatalogId;
  }

  protected getActiveTab(): PlatformCatalogPanelTab {
    const firstRouteChildUrl = this.activatedRoute.snapshot.firstChild?.url?.[0]?.path;

    if (firstRouteChildUrl === 'catalogs' || firstRouteChildUrl === 'organizations') {
      return firstRouteChildUrl;
    }

    return 'catalogs';
  }

  // routerLink funktioniert nicht mit dem Expand.
  protected navigateToTab(platformCatalogId: string, tab: PlatformCatalogPanelTab): void {
    this.internalNavigateToTab(platformCatalogId, tab);
  }

  protected handleKatalogExpanded(platformCatalogId: string): void {
    if (this.isCatalogActive(platformCatalogId)) {
      return;
    }

    this.internalNavigateToTab(platformCatalogId, 'catalogs');
  }

  private internalNavigateToTab(platformCatalogId: string, tab: string): void {
    this.router.navigate(['..', platformCatalogId, tab], {
      queryParams: this.plattformKatalogeFacade.getFilterQueryParamsForPlatformCatalog(platformCatalogId),
      relativeTo: this.activatedRoute,
    });
  }

  private internalNavigateToList(replaceUrl?: boolean): void {
    this.router.navigate(['..', ''], {
      queryParamsHandling: 'preserve',
      relativeTo: this.activatedRoute,
      replaceUrl,
    });
  }

  protected handleKatalogCollapsed(platformCatalogId: string): void {
    if (this.isCatalogActive(platformCatalogId)) {
      this.internalNavigateToList();
    }
  }

  handleFilterChanges(filter: PlatformCatalogGlobalFilter): void {
    this.plattformKatalogeFacade.updateGlobalFilter(filter);
  }

  handleCreated(createDto: CreatePlatformCatalog): void {
    this.plattformKatalogeFacade
      .createPlattformKatalog(createDto)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((platformCatalog) => {
        if (this.editMode === true) {
          this.editMode = null;
          this.internalNavigateToTab(platformCatalog.catalogId, 'catalogs');
        }
      });
  }

  setCreateMode(value: boolean): void {
    this.setEditModeCheckDirty(value ? true : null)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((confirmed) => {
        if (confirmed && value) {
          this.editModeFormControl.reset('');
        }
      });
  }

  publishPlattformKatalog(id: string): void {
    this.plattformKatalogeFacade.publishPlattformKatalog(id);
  }

  protected reindexPlatformCatalog({ catalogId }: PlatformCatalog): void {
    this.plattformKatalogeFacade.reindexPlattformkatalog(catalogId);
  }

  protected toggleMultipleDataSourcesSearchActive({
    catalogId,
    isSearchMultipleDataSourcesActive,
  }: PlatformCatalog): void {
    this.plattformKatalogeFacade
      .updatePlatformCatalog(catalogId, {
        isSearchMultipleDataSourcesActive: !isSearchMultipleDataSourcesActive,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  protected setEditMode(platformCatalog: PlatformCatalog): void {
    this.setEditModeCheckDirty(platformCatalog.catalogId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((confirmed) => {
        if (confirmed) {
          this.editModeFormControl.reset(platformCatalog.name);
        }
      });
  }

  protected cancelEditMode(platformCatalogId: string): void {
    if (this.editMode === platformCatalogId) {
      this.editMode = null;
    }
  }

  protected changePlatformCatalogName(platformCatalogId: string, name: string): void {
    this.plattformKatalogeFacade
      .updatePlatformCatalog(platformCatalogId, {
        name,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.editMode === platformCatalogId) {
          this.editMode = null;
        }
      });
  }

  protected deletePlatformCatalog(platformCatalog: PlatformCatalog): void {
    this.confirmDeletion(platformCatalog)
      .pipe(
        switchMap((confirmed) => {
          if (confirmed) {
            return this.plattformKatalogeFacade.deletePlatformCatalog(platformCatalog.catalogId);
          }
          return of(undefined);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        if (this.isCatalogActive(platformCatalog.catalogId)) {
          this.internalNavigateToList(true);
        }
      });
  }

  private setEditModeCheckDirty(targetMode: typeof this.editMode): Observable<boolean> {
    const obs$ =
      this.editMode === targetMode || !this.editModeFormControl.dirty || this.editMode == null
        ? of(true)
        : this.confirmUnsavedChanges();

    return obs$.pipe(
      tap((confirmed) => {
        if (confirmed) {
          this.editMode = targetMode;
        }
      }),
    );
  }

  private confirmUnsavedChanges(): Observable<boolean> {
    const dialogData: ConfirmDialogData = {
      title: 'Ungespeicherte Änderungen',
      message: 'Die aktuellen Änderungen wurden noch nicht gespeichert.',
      trueButtonText: 'Änderungen verwerfen und fortfahren',
      falseButtonText: 'Abbrechen',
    };

    return this.confirmDialogService.open(dialogData).afterClosed().pipe(filterNil());
  }

  private confirmDeletion(platformCatalog: PlatformCatalog): Observable<boolean> {
    const dialogData: ConfirmDialogData = {
      title: 'Plattformkatalog löschen',
      message: `Soll der Plattformkatalog "${platformCatalog.name}" wirklich gelöscht werden?`,
      trueButtonText: 'Löschen',
      falseButtonText: 'Abbrechen',
    };

    return this.confirmDialogService.open(dialogData).afterClosed().pipe(filterNil());
  }

  resetFilter(): void {
    this.plattformKatalogeFacade.resetFilter();
  }
}
